// Reduces a matrix-shaped gadget's rendered grid to the digest the Insights agent
// receives.
//
// This is the table the user is literally pointing at when they ask "why is
// BECFRA so far behind in loads" — so the source is the grid's own `rowsToDisplay`
// and `columnDefs`, not a re-derivation. Column headers supply the value labels,
// which means the agent sees the same words as the user ("Ahead/Behind", not
// "met_abc123").

import {
  InsightsViewValues,
  InsightsViewValueRow,
} from 'contextProviders/InsightsContentProvider/viewTypes';

import pinRows from './pinRows';

// Rows kept. A matrix can be hundreds of rows; head and tail cover the extremes
// of whatever the grid is currently sorted by, which is what a "why is X bad"
// question is about.
export const MAX_MATRIX_ROWS = 8;
const HALF = MAX_MATRIX_ROWS / 2;
// Value columns kept per row, so a wide grid doesn't dominate the whole budget.
export const MAX_MATRIX_COLUMNS = 6;

type Cell = unknown;

// A comparison grid stores each cell as a MatrixComparisonValue rather than a
// scalar, so unwrap before coercing.
const toNumber = (cell: Cell): number | undefined => {
  const raw =
    cell && typeof cell === 'object' && 'value' in cell
      ? (cell as { value: unknown }).value
      : cell;
  const value = typeof raw === 'string' ? Number(raw) : raw;
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
};

const toLabel = (cell: Cell): string | undefined => {
  const raw =
    cell && typeof cell === 'object' && 'value' in cell
      ? (cell as { value: unknown }).value
      : cell;
  return typeof raw === 'string' || typeof raw === 'number'
    ? String(raw)
    : undefined;
};

// ag-grid nests grouped columns under `children`; only leaves carry a `field`.
const flattenColumns = (columns: MatrixColDef[]): MatrixColDef[] =>
  columns.flatMap((column) =>
    column.children?.length ? flattenColumns(column.children) : [column],
  );

const sampleRows = <T>(rows: T[]): T[] =>
  rows.length <= MAX_MATRIX_ROWS
    ? rows
    : [...rows.slice(0, HALF), ...rows.slice(-HALF)];

const buildMatrixValuesDigest = ({
  chartDef,
  rows,
  columnDefs,
  asOf,
  pinnedLabels,
}: {
  chartDef: V5ChartDefinition;
  rows: (MatrixRowData | MatrixComparisonRowData)[];
  columnDefs: MatrixColDef[];
  asOf: string;
  // Rows that must survive sampling (the cell the user clicked); see
  // buildGadgetValuesDigest.
  pinnedLabels?: string[];
}): InsightsViewValues | undefined => {
  if (rows.length === 0) {
    return undefined;
  }

  const leafColumns = flattenColumns(columnDefs).filter(
    (column): column is MatrixColDef & { field: string } => !!column.field,
  );
  if (leafColumns.length === 0) {
    return undefined;
  }

  // The dimension column labels each row; every other leaf column is a value.
  // Fall back to the first column when the chart has no dimensionA (a reversed
  // matrix puts metrics down the side instead).
  const labelField = chartDef.dimensionA?.field ?? leafColumns[0].field;
  const valueColumns = leafColumns
    .filter((column) => column.field !== labelField)
    .slice(0, MAX_MATRIX_COLUMNS);
  if (valueColumns.length === 0) {
    return undefined;
  }

  const labelled = rows
    .map((row): InsightsViewValueRow | undefined => {
      const cells = row as { [key: string]: Cell };
      const label = toLabel(cells[labelField]);
      if (label === undefined) {
        return undefined;
      }
      const values = valueColumns
        .map((column) => ({
          metric: column.headerName || column.field,
          value: toNumber(cells[column.field]),
        }))
        .filter(
          (value): value is { metric: string; value: number } =>
            value.value !== undefined,
        );
      return values.length ? { label, values } : undefined;
    })
    .filter((row): row is InsightsViewValueRow => !!row);

  if (labelled.length === 0) {
    return undefined;
  }

  const sampled = pinRows({
    sampled: sampleRows(labelled),
    all: labelled,
    pinnedLabels,
  });

  return {
    asOf,
    rows: sampled,
    ...(labelled.length > sampled.length
      ? { rowsTruncatedFrom: labelled.length }
      : {}),
  };
};

export default buildMatrixValuesDigest;
